package org.multiplatform.kotfolio.pages.coroutines

import androidx.compose.runtime.*
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul

@Page("/coroutines/flow_create")
@Composable
fun FlowCreatePage() {
    BlogLayout(
        PageProperties(
            "Flow Create",
            ""
        ),
        BlogProperties("Coroutines", "Flow Create")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Flow are categorised into two types",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Intermediate operators",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "These operators create a new Flow and return it without triggering the collection of elements. They are not eagerly executed, meaning they don’t trigger the execution of the flow until a terminal operator is applied.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "They are used for transforming, filtering, or modifying the original Flow. filter, filterNot, filterNotNull, filterIsInstance, map, mapNotNull, withIndex, onEach, runningReduce, runningFold, scan, transform",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Terminal operators",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "These operators consume the Flow and trigger the execution of the entire flow. Once a terminal operator is applied, the flow starts emitting items, and the entire flow is processed.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Some of the terminal operators are: toList(), toSet(), count(), reduce(), fold(), collect().",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                WebImage(
                    src = Resources.Images.FLOW_BUILDER_CATEGORY_COROUTINES,
                    modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                    contentDescription = "Flow category"
                )
            }

            SpanText(
                text = "Create Flow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A flow builder is an interface we use to build a flow. There are different ways in which we can build a flow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "flow() - Sequential work",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It creates a cold flow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "flow() is a flow builder and will create a Flow<T>. Moreover, a flow is a reactive stream, as we mentioned at the beginning, flow is inspired by ReactiveX.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "builder function to construct arbitrary flaws from sequential calls to the emit function",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Cold flow means every time you call collect(), it will execute the block of flow(). Then, we use emit() in the block to send the value to upstream.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "flow() is executed after calling collect().",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "emit(...) to send values within the lambda block. will send a result to the flow. We know that flow can return multiple values, so you can call emit() multiple times to send multiple values.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = ".collect: The new emission will be queued if the current emission collected is still running.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "flowOf() - Static values",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Creates a new flow that generate sequence of values based on the provided arguments.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Can create a Flow that produces a specified value, or vararg values. We use it to define a flow with a fixed amount of values.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    fun main() = runBlocking {
                        flowOf(
                            "2025-06-05",
                            "2025-06-06",
                            "2025-06-07",
                            "2025-06-08",
                            "2025-06-09"
                        )
                            //.transform { emit(LocalDate.parse(it)) }
                            .transform {
                                emit("\n$it\n................")
                                eventsFor(it).forEach { event -> emit(event) }
                            }
                            //.filter { it.dayOfWeek !in setOf(DayOfWeek.SATURDAY, DayOfWeek.SUNDAY) }
                            //.take(5)
                            .collect { println(it) }
                    }
                    
                    fun main() = runBlocking {
                        flowOf(
                            "2025-06-05",
                            "2025-06-06",
                            "2025-06-07",
                            "2025-06-08",
                            "2025-06-09"
                        )
                            .map { LocalDate.parse(it) } // convert to LocalDate
                            .filter { it.dayOfWeek !in setOf(DayOfWeek.SATURDAY, DayOfWeek.SUNDAY) } // remove weekends
                            .map { it.toString() } // convert back to String
                            .transform {
                                emit("\n$it\n................")
                                eventsFor(it).forEach { event -> emit(event) }
                            }
                            .collect { println(it) }

                    }
                    
                    fun eventsFor(date: String): List<String> = when (date) {
                        "2025-06-04" -> listOf("Stand-up", "Interview Candidate")
                        "2025-06-05" -> listOf("Stand-up", "Sync-up with Jon")
                        "2025-06-06" -> listOf("Stand-up", "Product Demos")
                        "2025-06-07" -> listOf("Coffee at Jim's")
                        "2025-06-08" -> listOf("Family Luncheon")
                        "2025-06-09" -> listOf("Staff meeting", "Lunch with Libby", "Design Meeting")
                        "2025-06-10" -> listOf("Stand-up", "Haircut at Bert's")
                        "2025-06-11" -> listOf("Stand-up")
                        "2025-06-12" -> listOf("Stand-up", "Call with Vendor")
                        "2025-06-13" -> listOf("Stand-up")
                        else -> emptyList()
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "asFlow() - Collection → Flow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "asFlow() will create a hot flow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Hot flow means that the data is ready to be in the stream when the flow is created; while cold flow is when collect() is called, the flow() block will be executed, and emit() is called in the block to transfer the data to the stream. Therefore, hot flow is like a hot data source.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It is used to convert a collections, sequence, iterator, array or a range into a flow. extension function on various types to convert them into Flows.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Cold Flow: The flow starts from the beginning every time it is collected. It doesn’t cache or remember values.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Single-Use: It’s suitable for one-time consumption or situations where the data source is not continuously updated.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "asStateFlow()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "asStateFlow is an extension function for converting a mutable state holder, typically a MutableStateFlow, into a read-only StateFlow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State Management: Provides a read-only view of the state that can be observed by collectors.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State Preservation: Maintains the current state across multiple collectors.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Conflation: Can be configured to drop intermediate values if collectors are slow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "asSharedFlow()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "asSharedFlow is an extension function for converting a MutableSharedFlow into a read-only SharedFlow. Converts mutable shared flows into read-only shared flows, enabling multiple collectors to observe the same stream of data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Shared Emission: Allows multiple collectors to observe the same flow of data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Buffering and Conflation: Can buffer emitted values and control how to handle multiple slow collectors.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "emptyFlow()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "As the name hints, it is used when we want to create an empty flow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "channelFlow()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Builder function to construct arbitrary flows from potentially concurrent calls to the send function.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "MutableStateFlow and MutableSharedFlow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MutableStateFlow and MutableSharedFlow define the corresponding constructor functions to create a hot flow that can be directly updated.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "channelFlow() - (is cold)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = " channelFlow when you want to manually manage concurrency within the flow builder itself, such as merging multiple independent data sources simultaneously.\n",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "channelFlow cold? Yes. It won’t do anything until someone calls .collect(). However, once collected, it can act like a \"hot\" stream by producing values from multiple sources independently.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This builder creates flow with the elements using send provided by the builder itself.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Creates an instance of cold flow with the elements that are sent to a SendChannel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ChannelFlow produces the values in a separate coroutine without waiting for a receiver to complete processing on emitted items. Whereas normal flow works sequentially, emit() suspends until the consumer finishes working on the latest item.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                    val scope = CoroutineScope(Dispatchers.IO)

                    val flow = channelFlow {
                        println("send : 1")
                        send("1")
                        println("send : 2")
                        send("2")
                    }

                    scope.launch {
                        flow.collect {
                            println(it)
                            delay(5000)
                        }
                    }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                    fun main() {
                      
                        flowOf(1, 3, 9, 16)
                            .collect { print(it) }

                        //asFlow()
                        (1..10).asFlow()
                            .collect { print(it) ")}

                        //emptyFlow()
                        emptyFlow<Int>()
                           .collect { print(it) }    
                    }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                        class CustomerRepoImpl: CustomerRepository {
                            override fun readCustomerFlow(): Flow<RequestState<Customer>> = channelFlow {
                                send(RequestState.Success(data = customer))
                            }
                        }

                        interface CustomerRepository {
                            fun readCartFlow(): Flow<RequestState<List<Cart>>>
                        }
                               
                       private suspend fun observeCustomer() {
                            customerRepository.readCustomerFlow().collectLatest { data ->
                                when {
                                    data.isSuccess() -> { }
                                }
                            }
                       }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                    class AuthFirebaseDataSource(
                        private val firebaseAuth: FirebaseAuth
                    ) {
                        fun checkAuthStatus(): Flow<String> = channelFlow {
                            coroutineScope {
                                launch {
                                    firebaseAuth.authStateChanged.onEach {
                                        trySend(if(it == null) AuthStatus.UNAUTHORIZED.name else AuthStatus.AUTHORIZED.name)
                                    }.launchIn(this)
                                }
                            }
                        }
                    }
                    
                    class AuthRepositoryImpl(
                        private val authFirebaseDataSource: AuthFirebaseDataSource
                    ):AuthRepository {
                        override fun checkUserAuthorization(): Flow<String> {
                            return authFirebaseDataSource.checkAuthStatus()
                        }
                    }
                    
                    interface AuthRepository {
                        fun checkUserAuthorization(): Flow<String>
                    }
                    
                   
                    class AuthViewModel(): ViewModel() {
                        private val authRepository = Inject.di.get<AuthRepository>()

                        private val _uiAction:MutableStateFlow<AuthUIAction> = MutableStateFlow(AuthUIAction.None)
                        val uiAction = _uiAction.asCommonFlow()

                        init {
                            authRepository.checkUserAuthorization().onEach {
                                if(it == AuthStatus.AUTHORIZED.name){
                                    _uiAction.value = AuthUIAction.OpenPortfolioScreen
                                }
                                return@onEach
                            }.launchIn(viewModelScope)
                        }
                    }
                    
                    sealed class AuthUIAction {
                        data object OpenPortfolioScreen:AuthUIAction()
                        data object None:AuthUIAction()
                    }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                    //https://github.com/CodeInKotLang/MeasureMate (Firebase Login, Flow, Create User, Combine)
                        
                    class AuthRepositoryImpl(
                        private val firebaseAuth: FirebaseAuth,
                        private val credentialManager: CredentialManager
                    ): AuthRepository {
                        override val authStatus: Flow<AuthStatus> = callbackFlow {
                            val authStateListener = FirebaseAuth.AuthStateListener { auth ->
                                val status = if (auth.currentUser != null) {
                                    AuthStatus.AUTHORISED
                                } else AuthStatus.UNAUTHORISED
                                trySend(status)
                            }
                            firebaseAuth.addAuthStateListener(authStateListener)
                            awaitClose {
                                firebaseAuth.removeAuthStateListener(authStateListener)
                            }
                        }.distinctUntilChanged()
                    }
                    
                    interface AuthRepository {
                        val authStatus: Flow<AuthStatus>
                    }
                    
                    val authStatus = authRepository.authStatus
                    .stateIn(
                        scope = viewModelScope,
                        started = SharingStarted.WhileSubscribed(stopTimeoutMillis = 5000),
                        initialValue = AuthStatus.LOADING
                    )
                    
                    enum class AuthStatus {
                        LOADING,
                        UNAUTHORISED,
                        AUTHORISED
                    }
                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin flow operators are functions that can transform, combine, filter, or manipulate the values emitted by a flow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "callbackFlow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "callbackFlow is a function that allows you to create a flow from a callback-based API. This is an efficient way to handle multiple asynchronous events and output values as they are received. Callback flow is cold flow, which can be used as an existing callback to a flow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "callbackFlow ensures that awaitClose is called inside lambda(awaitClose is mandatory in callbackFlow unlike channelFlow ) keeping Flow alive, to send data any point later and to be able to unregister any listeners when its canceled.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Choose callbackFlow",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "callbackFlow is specifically designed to wrap listener-based or callback-based APIs into a reactive stream.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If you want to read a single shot of data then you might not need callbackFlow, a single shot you can still get from normal Flow or coroutines and does not need to implement callbackFlow for that.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "callbackFlow is an ideal choice where we want to read data periodically from any callbacks E.g following are applications (but not limited to) of callbackFlow",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Reading Network Status on Device — (multi-shot)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Reading Device Location Data — (multi-shot)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Handling any Third Party library callbacks which is providing data real time e.g Firebase callbacks — (multi-shot)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "How it works",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Create a CallbackFlow: Define a callbackFlow block where you'll handle the callback logic.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Emit Data: Inside the block, use the send or trySend function to emit values from the callback. The send function is used to emit values from the callback. You can use trySend for non-blocking emission, but be cautious about potential buffer overflows.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Close the Flow: When the callback is complete or cancelled, close the flow using channel.close().",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Firebase RealtimeDatabaseas an example to show usage of callbackFlow with a real stream of data. At the end of the story we can apply this knowledge to convert any library callbacks to Kotlin Flows using callbackFlow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                         sealed class RepositoryState<out T> {
                             data class Success<T>(val data: T) : RepositoryState<T>()
                             data class Failure(val exception: Exception) : RepositoryState<Nothing>()
                         }

                         data class Note(
                             val title: String = "",
                             val description: String = "",
                             val timestamp: Long = 0,
                             val completed: Boolean = false,
                             val archived: Boolean = false,
                             val id: Int = 0
                         )

                         interface IRepository {
                             fun getTodoNote(): Flow<RepositoryState<List<Note>>>
                         }

                         class RepositoryImpl @Inject constructor(
                             private val realtimeDatabase: IRealtimeDatabase
                         ) : IRepository {

                             override fun getTodoNote(): Flow<RepositoryState<List<Note>>> {
                                 return flow {
                                     realtimeDatabase.getTodoNote().collect { result ->
                                         try {
                                             when (result) {
                                                 is RealtimeDatabaseState.Success -> {
                                                     emit(RepositoryState.Success(result.data))
                                                 }

                                                 is RealtimeDatabaseState.Failure -> {
                                                     emit(RepositoryState.Failure(result.exception))
                                                 }
                                             }

                                         } catch (e: Exception) {
                                             emit(RepositoryState.Failure(e))
                                         }
                                     }
                                 }.flowOn(Dispatchers.IO)
                             }
                         }

                         private fun getTodoNote() {
                             viewModelScope.launch(Dispatchers.Main) {
                                 repository.getTodoNote()
                                     .flowOn(Dispatchers.IO)
                                     .collect { result ->
                                         when (result) {
                                             is RepositoryState.Success -> {
                                                 _noteItemResult.value = NoteUIState.Success(result.data)
                                             }

                                             is RepositoryState.Failure -> {
                                                 _noteItemResult.value = NoteUIState.Error(result.exception.toString())
                                             }
                                         }
                                     }
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import kotlinx.coroutines.flow.callbackFlow
                    import kotlinx.coroutines.flow.collect

                    fun fetchData(callback: (String) -> Unit) {
                        // Simulate asynchronous operation
                        Thread {
                            Thread.sleep(2000)
                            callback("Data fetched")
                        }.start()
                    }

                    fun main() {
                        val flow = callbackFlow<String> {
                            fetchData { data ->
                                send(data)
                            }
                        }

                        flow.collect { data ->
                            println(data) // Output: Data fetched
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    callbackFlow<String> {
                        try {
                            fetchData { data ->
                                send(data)
                            }
                        } catch (e: Exception) {
                            throw e
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    callbackFlow<String> {
                        try {
                            fetchData { data ->
                                if (isActive) {
                                    send(data)
                                }
                            }
                        } finally {
                            tryCancel()
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    callbackFlow<String> {
                        val emitter1 = launch {
                            // Emit values from emitter1
                        }
                        val emitter2 = launch {
                            // Emit values from emitter2
                        }
                        awaitAll(emitter1, emitter2)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import android.content.Context
                    import android.net.ConnectivityManager
                    import android.net.Network
                    import android.net.NetworkCapabilities
                    import android.net.NetworkRequest
                    import android.os.Build
                    import androidx.compose.runtime.Stable
                    import androidx.core.content.getSystemService
                    import dagger.hilt.android.qualifiers.ApplicationContext
                    import kotlinx.coroutines.channels.awaitClose
                    import kotlinx.coroutines.flow.Flow
                    import kotlinx.coroutines.flow.callbackFlow
                    import kotlinx.coroutines.flow.conflate
                    import javax.inject.Inject

                    @Stable
                     class ConnectivityManagerNetworkMonitor @Inject constructor(
                        @ApplicationContext private val context: Context,
                    ) : NetworkMonitor {
                        override val isOnline: Flow<Boolean> = callbackFlow {
                            val connectivityManager = context.getSystemService<ConnectivityManager>()
                            if (connectivityManager == null) {
                                channel.trySend(false)
                                channel.close()
                                return@callbackFlow
                            }

                            val callback = object : ConnectivityManager.NetworkCallback() {

                                private val networks = mutableSetOf<Network>()

                                override fun onAvailable(network: Network) {
                                    networks += network
                                    channel.trySend(true)
                                }

                                override fun onLost(network: Network) {
                                    networks -= network
                                    channel.trySend(networks.isNotEmpty())
                                }
                            }

                            val request = NetworkRequest.Builder()
                                .addCapability(NetworkCapabilities.NET_CAPABILITY_INTERNET)
                                .build()
                            connectivityManager.registerNetworkCallback(request, callback)

                            channel.trySend(connectivityManager.isCurrentlyConnected())

                            awaitClose {
                                connectivityManager.unregisterNetworkCallback(callback)
                            }
                        }
                            .conflate()

                        private fun ConnectivityManager.isCurrentlyConnected() = activeNetwork
                            ?.let(::getNetworkCapabilities)
                            ?.hasCapability(NetworkCapabilities.NET_CAPABILITY_INTERNET) ?: false
                    }
                    
                    @Stable
                    interface NetworkMonitor {
                        val isOnline: Flow<Boolean>
                    }
                   
                    @Module
                    @InstallIn(SingletonComponent::class)
                    abstract class DataModule {

                        @Binds
                        internal abstract fun bindsNetworkMonitor(
                            networkMonitor: ConnectivityManagerNetworkMonitor,
                        ): NetworkMonitor
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import android.content.Context
                    import android.net.ConnectivityManager
                    import android.net.Network
                    import android.net.NetworkCapabilities
                    import android.net.NetworkRequest
                    import androidx.core.content.getSystemService
                    import kotlinx.coroutines.channels.awaitClose
                    import kotlinx.coroutines.flow.Flow
                    import kotlinx.coroutines.flow.callbackFlow
                    import kotlinx.coroutines.flow.conflate

                    val Context.isOnline: Flow<Boolean>
                        get() = callbackFlow {
                            val connectivityManager = getSystemService<ConnectivityManager>()
                            if (connectivityManager == null) {
                                channel.trySend(false)
                                channel.close()
                                return@callbackFlow
                            }

                            val networkCallback = object : ConnectivityManager.NetworkCallback() {
                                val networks = mutableSetOf<Network>()

                                override fun onAvailable(network: Network) {
                                    networks += network
                                    channel.trySend(true)
                                }

                                override fun onLost(network: Network) {
                                    networks -= network
                                    channel.trySend(networks.isNotEmpty())
                                }
                            }

                            val networkRequest = NetworkRequest.Builder()
                                .addCapability(NetworkCapabilities.NET_CAPABILITY_INTERNET)
                                .build()

                            connectivityManager.registerNetworkCallback(networkRequest, networkCallback)

                            /**
                             *send the latest connectivity status to the underlying channel
                             *  */

                            /**
                             *send the latest connectivity status to the underlying channel
                             *  */
                            channel.trySend(connectivityManager.isNetworkActive)

                            awaitClose {
                                connectivityManager.unregisterNetworkCallback(networkCallback)
                            }
                        }
                            .conflate()

                    val ConnectivityManager.isNetworkActive: Boolean
                        get() = run {
                            val netCapabilities = getNetworkCapabilities(activeNetwork)

                            netCapabilities?.run {
                                hasCapability(NetworkCapabilities.NET_CAPABILITY_IMS) //vpn
                                        || hasCapability(NetworkCapabilities.NET_CAPABILITY_MMS) //cellular
                                        || hasCapability(NetworkCapabilities.NET_CAPABILITY_SUPL) //wifi
                            } ?: false
                        }
                    """.trimIndent()
            )

            SpanText(
                text = "Continuous Data Stream",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    fun fetchStockPrices(symbol: String): Flow<Double> = flow {
                        while (true) { // Infinite stream
                            delay(500)
                            val price = api.fetchPrice(symbol) // Suspending API call
                            emit(price)
                        }
                    }.flowOn(Dispatchers.IO) // Offload to background thread
                    """.trimIndent()
            )
        }
    }
}
